<script lang="ts">
  import { onMount } from 'svelte';
  import { Home, FolderOpen, FileText } from 'lucide-svelte';
  
  let activeSection = 'introduction';
  let isExpanded = false;
  let isAnimating = false;
  let isClosing = false;
  let nav: HTMLElement;
  let navBorderRadius = '50px';
  let navWidth = '62px';
  let animationFrameId: number | null = null;
  
  const navItems = [
    { id: 'introduction', label: 'Introduction', icon: Home },
    { id: 'projects', label: 'Public Projects', icon: FolderOpen },
    { id: 'blog', label: 'Blog', icon: FileText }
  ];
  
  function scrollToSection(sectionId: string) {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }
  
  function handleMouseEnter() {
    if (window.innerWidth > 768) {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
      
      isExpanded = true;
      isAnimating = true;
      isClosing = false;
      
      animateBorderRadius(parseFloat(navBorderRadius), 30, 10, 'linear');
      
      navWidth = '200px';
      
      setTimeout(() => {
        isAnimating = false;
      }, 500);
    }
  }
  
  function handleMouseLeave() {
    if (window.innerWidth > 768) {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
      
      isAnimating = true;
      isClosing = true;
      
      animateBorderRadius(parseFloat(navBorderRadius), 50, 400, 'custom');
      
      setTimeout(() => {
        navWidth = '62px';
        
        setTimeout(() => {
          isExpanded = false;
          isAnimating = false;
          isClosing = false;
        }, 10);
      }, 10);
    }
  }
  
  function animateBorderRadius(start: number, end: number, duration: number, easingType: 'linear' | 'custom') {
    const startTime = performance.now();
    
    function updateBorderRadius(currentTime: number) {
      const elapsedTime = currentTime - startTime;
      let progress = Math.min(elapsedTime / duration, 1);
      
      if (easingType === 'custom') {
        progress = customEaseIn(progress);
      }
      
      const currentRadius = start + (end - start) * progress;
      
      navBorderRadius = `${currentRadius}px`;
      
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(updateBorderRadius);
      } else {
        animationFrameId = null;
      }
    }
    
    animationFrameId = requestAnimationFrame(updateBorderRadius);
  }
  
  function customEaseIn(t: number): number {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }
  
  onMount(() => {
    const sections = document.querySelectorAll('.section');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            activeSection = entry.target.id;
          }
        });
      },
      { threshold: 0.5 }
    );
    
    sections.forEach((section) => observer.observe(section));
    
    return () => observer.disconnect();
  });
</script>

<nav
  bind:this={nav}
  class="side-nav glass"
  class:expanded={isExpanded}
  on:mouseenter={handleMouseEnter}
  on:mouseleave={handleMouseLeave}
  style="border-radius: {navBorderRadius}; width: {navWidth};"
>
  <div class="nav-content">
    {#each navItems as item}
      <button
        class="nav-item"
        class:active={activeSection === item.id}
        on:click={() => scrollToSection(item.id)}
      >
        <div class="nav-icon">
          <svelte:component this={item.icon} size={20} />
        </div>
        <span class="nav-label">{item.label}</span>
      </button>
    {/each}
  </div>
</nav>

<style>
  .side-nav {
    position: fixed;
    top: 50%;
    left: var(--spacing-lg);
    transform: translateY(-50%);
    height: auto;
    padding: var(--spacing-xs);
    transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    flex-direction: column;
    justify-content: center;
    z-index: 100;
    overflow: hidden;
    font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif;
  }
  
  .nav-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
  }
  
  .nav-item {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    width: 100%;
    padding: 13px 10px;
    background: transparent;
    border: none;
    border-radius: 30px;
    color: var(--text-tertiary);
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
  }
  
  .nav-item:hover {
    background: rgba(255, 255, 255, 0.05);
    color: var(--text-secondary);
    transform: translateX(2px);
  }
  
  .nav-item.active {
    color: var(--text-primary);
    background: rgba(90, 209, 151, 0.15);
  }
  
  .nav-item.active .nav-icon {
    filter: drop-shadow(0 0 8px var(--accent-orange-glow));
  }
  
  .nav-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    transition: filter 0.3s ease;
  }
  
  .nav-label {
    font-weight: 500;
    white-space: nowrap;
    opacity: 0;
    transform: translateX(-10px);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 0.875rem;
    padding-right: 8px;
    font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif;
  }
  
  .expanded .nav-label {
    opacity: 1;
    transform: translateX(0);
  }
  
  @media (max-width: 768px) {
    .side-nav {
      height: auto;
      width: auto !important;
      bottom: var(--spacing-lg);
      top: auto;
      left: 50%;
      transform: translateX(-50%);
      padding: var(--spacing-xs);
      border-radius: 50px !important;
    }
    
    .nav-content {
      flex-direction: row;
      justify-content: center;
    }
    
    .nav-item {
      flex: 1;
      justify-content: center;
      padding: 8px;
    }
    
    .nav-label {
      display: none;
    }
  }
</style>